import React, { useState, useEffect, useRef } from "react";
import { Col, Row, Spin, message } from "antd";
import { convertToRGBAString } from "utils/helperFunctions";
import { IoPause, IoPlay, IoTrash } from "react-icons/io5";
import { IoIosMic } from "react-icons/io";
import { memo } from "react";
import { fontSize } from "utils/CONSTANTS";
import moment from "moment";
import { uploadMedia } from "api";
import { LoadingOutlined } from "@ant-design/icons";
import { setButtonDesible } from "Redux/actions";
import { useDispatch } from "react-redux";

const initialVoiceStatus = {
  status: "record",
  duration: 0,
  position: 0,
  url: null,
  chunk: [],
  press: false,
  ended: false,
};

const Header = memo(({ item, selectedLanguage }) => {
  return (
    <>
      <Col
        xs={24}
        className="form-item-card-header"
        style={{
          backgroundColor: convertToRGBAString(
            item.label_bg_color || [255, 255, 255, 1]
          ),
        }}
      >
        <div className="flex-items-row centered">
          <span
            className="font-helvetica-neue-medium"
            style={{
              fontSize: fontSize[item.label_text_size || "S"],
              color: convertToRGBAString(
                item.label_text_color || [117, 117, 117, 1]
              ),
              textAlign: "center",
            }}
          >
            {item.placeholder[selectedLanguage]}
          </span>
        </div>
      </Col>
      {item?.label_bg_color?.every(
        (val, index) => val === [255, 255, 255, 1][index]
      ) && (
        <Col xs={24} style={{ height: "1px" }}>
          <hr className="twelve" />
        </Col>
      )}
    </>
  );
});

const Main = memo(({ item, form, selectedLanguage }) => {
  const [voiceStatus, setVoiceStatus] = useState(initialVoiceStatus);
  const [recorder, setRecorder] = useState(null);
  const [audio, setAudio] = useState(null);
  const [intervals, setIntervals] = useState([]);
  const [wave, setwave] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (voiceStatus.url && !form.getFieldValue(item.name)) {
      form.setFieldsValue({
        [item.name]: voiceStatus.url,
      });
    }
  }, [voiceStatus.url, form, item]);

  useEffect(() => {
    if (recorder !== null && voiceStatus.press) {
      if (recorder.state === "inactive") {
        recorder.start(100);
      }
      recorder.ondataavailable = (event) => {
        setVoiceStatus((prevState) => {
          const newState = {
            ...prevState,
            chunk: [...prevState.chunk, event.data],
            duration: prevState.duration + 100,
          };
          return newState;
        });
      };
    }
  }, [recorder, voiceStatus]);

  useEffect(() => {
    if (audio !== null) {
      audio.ontimeupdate = () => {
        setVoiceStatus((prevStatus) => {
          if (!prevStatus.ended && prevStatus.status !== "record") {
            return {
              ...prevStatus,
              position: prevStatus.position + 200,
            };
          }
          return prevStatus;
        });
      };

      audio.onended = () => {
        audio.pause();
        audio.currentTime = 0;
        setVoiceStatus((prevStatus) => ({
          ...prevStatus,
          status: "play",
          position: 0,
          ended: true,
        }));
      };
    }
  }, [audio]);

  const handleClick = (type) => {
    switch (type) {
      case "record": {
        if (!recorder) {
          dispatch(setButtonDesible(true));
          setVoiceStatus({ ...voiceStatus, status: "upload", press: true });
          navigator.mediaDevices
            .getUserMedia({
              audio: true,
              video: false,
            })
            .then((stream) => {
              setRecorder(new MediaRecorder(stream));
              const context = new AudioContext();
              const analyser = context.createAnalyser();
              analyser.fftSize = 128;
              const streamNode = context.createMediaStreamSource(stream);
              streamNode.connect(analyser);
              setIntervals([
                ...intervals,
                setInterval(() => {
                  const data = new Uint8Array(analyser.frequencyBinCount);
                  analyser.getByteFrequencyData(data);
                  setwave((prevwave) => {
                    const newWave = [...prevwave];
                    newWave.push(
                      Math.round(
                        data.reduce((prev, initial) => prev + initial, 0) / 200
                      )
                    );
                    return newWave;
                  });
                }, 100),
              ]);
            })
            .catch(() => {
              console.log("Access error");
            });
        }
        break;
      }
      case "upload": {
        if (recorder && recorder.state !== "inactive") {
          recorder.stop();
          intervals.forEach((interval) => clearInterval(interval));
          if (voiceStatus.duration >= 1000) {
            setVoiceStatus({
              ...voiceStatus,
              status: "uploading",
              press: false,
            });
            let newState = { ...initialVoiceStatus };
            const blob = new Blob(voiceStatus.chunk, { type: "audio/m4a" });
            const formData = new FormData();
            const requestName = `${item.name}-->f`;
            formData.append(requestName, blob, "audio.m4a");
            uploadMedia({ data: formData })
              .then((responce) => {
                const { data } = responce || {};
                const url = data[0][requestName];
                setAudio(new Audio(url));
                newState = {
                  ...voiceStatus,
                  url,
                  status: "play",
                  press: false,
                };
              })
              .catch(() => {
                message.error("ERROR! Something went wrong");
              })
              .finally(() => {
                setVoiceStatus(newState);
                dispatch(setButtonDesible(false));
              });
          } else {
            setVoiceStatus(initialVoiceStatus);
            setRecorder(null);
            setwave([]);
            dispatch(setButtonDesible(false));
          }
        }
        break;
      }
      case "play": {
        audio.play();
        setVoiceStatus({ ...voiceStatus, status: "pause", ended: false });
        break;
      }
      case "pause": {
        audio.pause();
        setVoiceStatus({ ...voiceStatus, status: "play" });
        break;
      }
      case "delete": {
        audio.pause();
        setVoiceStatus(initialVoiceStatus);
        setRecorder(null);
        setwave([]);
        form.setFieldsValue({
          [item.name]: null,
        });
        break;
      }
      default: {
        console.log("Error from handleClick: Wrong argument");
      }
    }
  };

  const props = { voiceStatus, handleClick, item, selectedLanguage };
  return (
    <Col xs={24} className="form-item-card-body">
      <Row gutter={[10, 10]}>
        <Duration {...{ ...props, ...{ wave } }} />
        <Button {...props} />
      </Row>
    </Col>
  );
});

const Button = memo(({ voiceStatus, handleClick, item, selectedLanguage }) => {
  const { status, duration } = voiceStatus;

  useEffect(() => {
    if (duration >= 30000) {
      handleClick("upload");
    }
  }, [duration, handleClick]);

  const buttonStyle = {
    color: convertToRGBAString(item.audio_attrs.button_icon_color),
  };
  const LoaderIcon = () => (
    <Spin
      className="voice_record_block_button--loading"
      indicator={<LoadingOutlined spin style={buttonStyle} />}
    />
  );

  const buttonList = {
    record: {
      text: item.audio_attrs.input_record_text[selectedLanguage] || "",
      icon: IoIosMic,
    },
    upload: {
      text: item.audio_attrs.input_recording_text[selectedLanguage] || "",
      icon: IoIosMic,
    },
    uploading: {
      text: item.audio_attrs.input_upload_text[selectedLanguage] || "",
      icon: LoaderIcon,
    },
    play: {
      text: item.audio_attrs.input_play_text[selectedLanguage] || "",
      icon: IoPlay,
    },
    pause: {
      text: item.audio_attrs.input_pause_text[selectedLanguage] || "",
      icon: IoPause,
    },
  };
  const SelectedButton = buttonList[status].icon;
  return (
    <Col xs={24} className="voice_record_block">
      <button
        className={`voice_record_block_button ${
          voiceStatus.press && "voice_record_block_button--recording"
        }`}
        onClick={() => handleClick(status)}
        style={{
          backgroundColor: convertToRGBAString(item.audio_attrs.button_color),
        }}
      >
        <SelectedButton
          className="voice_record_block_button_icon"
          style={buttonStyle}
        />
      </button>
      <p
        className="voice_record_block_text"
        style={{ color: convertToRGBAString(item.audio_attrs.text_color) }}
      >
        {buttonList[status].text}
      </p>
    </Col>
  );
});
const Duration = memo(({ voiceStatus, handleClick, item, wave }) => {
  const waveRef = useRef(null);
  const [waveWidth, setWaveWidth] = useState(0);

  useEffect(() => {
    if (
      waveRef.current &&
      (voiceStatus.status === "record" || voiceStatus.status === "play")
    ) {
      setWaveWidth(waveRef.current.offsetWidth);
    }
  }, [voiceStatus.status, waveRef]);

  useEffect(() => {
    if (waveRef.current) {
      const ctx = waveRef.current.getContext("2d");
      ctx.clearRect(0, 0, waveWidth, 50);
      const position =
        (waveWidth * voiceStatus.position * 100) / (voiceStatus.duration * 100);
      // ============== Wave Time Line ==============
      ctx.beginPath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = convertToRGBAString(
        item.audio_attrs.wave_passive_color
      );
      ctx.moveTo(0, 25);
      ctx.lineTo(waveWidth, 25);
      ctx.stroke();

      if (wave.length !== 0) {
        if (position > 1) {
          ctx.beginPath();
          ctx.strokeStyle = convertToRGBAString(
            item.audio_attrs.wave_active_color
          );
          ctx.moveTo(0, 25);
          ctx.lineTo(position, 25);
          ctx.stroke();
        }

        // ================ Wave Slice ================
        const arrayLength = Math.floor(waveWidth / 5);
        let waveSlice = wave.slice(-arrayLength);

        if (["play", "pause"].includes(voiceStatus.status)) {
          if (arrayLength > waveSlice.length) {
            let waveSliceGap = Math.ceil(
              (arrayLength - wave.length) / wave.length
            );
            const newWave = new Array(arrayLength);
            let statisIndex = 0;

            waveSlice = newWave.fill().map((_, index) => {
              if (index % waveSliceGap === 0 && waveSlice[statisIndex + 1]) {
                statisIndex++;
              }
              return waveSlice[statisIndex];
            });
          } else {
            let waveSliceGap = Math.ceil(
              (wave.length - arrayLength) / arrayLength
            );
            waveSlice.map((_, index) => wave[index * waveSliceGap]);
          }
        }

        waveSlice.forEach((slice, index) => {
          slice = slice === 0 ? 3 : slice;
          const color =
            position !== 0 && index * 4.3 <= position
              ? convertToRGBAString(item.audio_attrs.wave_active_color)
              : convertToRGBAString(item.audio_attrs.wave_passive_color);
          ctx.beginPath();
          ctx.strokeStyle = color;
          ctx.fillStyle = color;
          ctx.lineWidth = 3;
          ctx.moveTo(index * 5, (50 - slice) / 2);
          ctx.lineTo(index * 5, (50 - slice) / 2 + slice);
          ctx.stroke();
        });
      }
    }
  }, [wave, voiceStatus, waveWidth, item]);

  return (
    <Col xs={24}>
      <Row>
        <Col xs={24} className="voice_header_duration_second_block ">
          <span
            style={{ color: convertToRGBAString(item.audio_attrs.text_color) }}
          >
            {moment(
              voiceStatus.position ? voiceStatus.position : voiceStatus.duration
            ).format("mm:ss")}
          </span>
        </Col>
        <Col xs={24} className="voice_header_duration_timeline_block">
          <canvas
            height={50}
            width={waveWidth}
            className="voice_header_duration_timeline_block_wave"
            ref={waveRef}
          >
            Opps! Your browser does not support the HTML5 canvas tag.
          </canvas>
          {!["record", "upload", "uploading"].includes(voiceStatus.status) && (
            <button
              className="voice_header_duration_timeline_block_delete"
              onClick={() => handleClick("delete")}
            >
              <IoTrash
                style={{
                  color: convertToRGBAString(item.audio_attrs.button_color),
                }}
              />
            </button>
          )}
        </Col>
      </Row>
    </Col>
  );
});

const VoiceRecorder = ({ component, item, selectedLanguage, form }) => {
  const props = { item, selectedLanguage, component };
  return (
    <Row className="form-item-card">
      <Header {...props} />
      <Main {...props} {...{ form }} />
    </Row>
  );
};

export default VoiceRecorder;
